import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ensureCsrfToken, getCsrfHeader } from '../utils/csrf';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialToken = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [csrfReady, setCsrfReady] = useState(false);

  useDocumentTitle('Cognidaily | Set New Password');

  // Fetch CSRF token on mount (users coming from email won't have one)
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        await ensureCsrfToken();
        setCsrfReady(true);
      } catch (err) {
        console.error('Failed to fetch CSRF token:', err);
        // Still allow form submission - CSRF middleware will handle errors
        setCsrfReady(true);
      }
    };
    fetchCsrfToken();
  }, []);

  const validateForm = (): boolean => {
    if (!initialToken) {
      setError('Reset link is missing or invalid. Please request a new link.');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (password.length > 128) {
      setError('Password must be no more than 128 characters long');
      return false;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])(?!.*\s).{8,128}$/;
    if (!passwordRegex.test(password)) {
      setError(
        'Password must be 8-128 characters and include uppercase, lowercase, number, and symbol (no spaces)'
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Ensure CSRF token is available before making request
      await ensureCsrfToken();
      const csrfHeader = getCsrfHeader();
      
      const response = await axios.post('/api/auth/reset-password', {
        token: initialToken,
        password
      }, {
        headers: csrfHeader
      });
      const payload = response.data;

      if (payload.success) {
        setSuccess('Your password has been reset. You can now sign in.');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(
          payload.error ||
            payload.message ||
            'Unable to reset password. Please try again.'
        );
      }
    } catch (err: any) {
      const message: string | undefined =
        err.response?.data?.error || err.response?.data?.message;
      setError(
        message || 'Unable to reset password. Please request a new link.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section section--auth">
        <h1>Set a new password</h1>
        <p>Choose a strong password (8+ chars, upper, lower, number, symbol).</p>
        {error && <p role="alert" className="text-error">{error}</p>}
        {success && <p>{success}</p>}
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="password" className="form-label">New password</label>
            <div className="password-input-wrapper">
              <input type={showPassword ? 'text' : 'password'} id="password" className="form-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} disabled={loading} />
              <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? 'Hide' : 'Show'}</button>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">Confirm password</label>
            <div className="password-input-wrapper">
              <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" className="form-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={loading} />
              <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>{showConfirmPassword ? 'Hide' : 'Show'}</button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || !csrfReady}>
            {loading ? 'Updating...' : !csrfReady ? 'Loading...' : 'Update password'}
          </button>
        </form>
        <p><Link to="/login" className="link">Back to sign in</Link></p>
    </section>
  );
};

export default ResetPassword;











